import axios from 'axios'

import type { ProblemDetails } from '@/types/api'

/** Transport-agnostic error every feature can rely on. */
export class ApiError extends Error {
  readonly status: number
  readonly detail: string | undefined
  /** Validation failures keyed by the API's PascalCase property names. */
  readonly fieldErrors: Record<string, string[]> | undefined

  constructor(params: {
    message: string
    status: number
    detail?: string | undefined
    fieldErrors?: Record<string, string[]> | undefined
  }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.detail = params.detail
    this.fieldErrors = params.fieldErrors
  }

  get isUnauthorized() {
    return this.status === 401
  }

  get isForbidden() {
    return this.status === 403
  }

  get isNotFound() {
    return this.status === 404
  }

  get isValidation() {
    return this.status === 400 && Boolean(this.fieldErrors)
  }

  /** 4xx other than 408/429 are caller mistakes — retrying will not help. */
  get isRetryable() {
    if (this.status === 0 || this.status >= 500) return true
    return this.status === 408 || this.status === 429
  }

  /**
   * Looks a field up case-insensitively, so callers can pass camelCase names
   * while the API answers in PascalCase.
   */
  fieldError(field: string): string | undefined {
    if (!this.fieldErrors) return undefined
    const key = Object.keys(this.fieldErrors).find(
      (candidate) => candidate.toLowerCase() === field.toLowerCase(),
    )
    return key ? this.fieldErrors[key]?.[0] : undefined
  }
}

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.'

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (axios.isAxiosError<ProblemDetails>(error)) {
    const status = error.response?.status ?? 0
    const problem = error.response?.data
    return new ApiError({
      message: problem?.title ?? error.message ?? DEFAULT_MESSAGE,
      status,
      detail: problem?.detail ?? undefined,
      fieldErrors: problem?.errors,
    })
  }

  if (error instanceof Error) {
    return new ApiError({ message: error.message, status: 0 })
  }

  return new ApiError({ message: DEFAULT_MESSAGE, status: 0 })
}
